import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { UseQueryResult } from "@tanstack/react-query";
import { Crown, RefreshCw, Search, ShieldCheck, ShieldOff, UserMinus, UserPlus, UserX } from "lucide-react";
import { formatAccountNo, useAccounts } from "@/lib/accounts";
import { useCurrentUser } from "@/lib/auth";
import {
  ADMIN_RIGHTS,
  type AdminRights,
  type AdminTarget,
  LIMITS,
  type OwnChat,
  type Person,
  accountOptionLabel,
  canAct,
  defaultAdminRights,
  errorText,
  refOf,
  settle,
  useChatAdmins,
  useChatMembers,
  useMoveChatMember,
  useRemoveChatAdmin,
  useSetChatAdmin,
  useTransferOwnChat,
} from "@/lib/ownChats";
import { pushToast } from "@/components/Toaster";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ConfirmDialog,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ActionMenu, type ActionMenuItem } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

/**
 * A list read from Telegram: says that it is being read (it takes seconds),
 * or why it could not be, with a way to ask again; then shows it.
 */
export function Live<T>({
  query,
  children,
}: {
  query: UseQueryResult<T>;
  children: (data: T) => ReactNode;
}) {
  const { t } = useTranslation();
  if (query.isFetching) {
    return <p className="px-1 py-6 text-center text-sm text-fg-muted">{t("own_chats.reading")}</p>;
  }
  return (
    <>
      {/* A read that failed says so even over a list read earlier: what is
          shown under it is then the old one. */}
      {query.isError && (
        <Alert
          tone="danger"
          action={
            <Button size="sm" variant="outline" onClick={() => query.refetch()}>
              {t("own_chats.retry")}
            </Button>
          }
        >
          {errorText(query.error, t("own_chats.load_error"))}
        </Alert>
      )}
      {query.data !== undefined && children(query.data)}
    </>
  );
}

/** "Read again" for a list that is otherwise read once. */
export function RereadButton({ query }: { query: UseQueryResult<unknown> }) {
  const { t } = useTranslation();
  return (
    <Button
      size="sm"
      variant="ghost"
      disabled={query.isFetching}
      onClick={() => query.refetch()}
      iconLeft={<RefreshCw size={14} strokeWidth={2} aria-hidden="true" />}
    >
      {t("own_chats.reread")}
    </Button>
  );
}

export function PersonLine({
  person,
  note,
  actions,
}: {
  person: Person;
  note?: ReactNode;
  actions?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="truncate text-sm font-medium text-fg-primary">{person.name}</span>
          {person.role === "creator" && (
            <Badge tone="accent" icon={<Crown size={11} strokeWidth={2} aria-hidden="true" />}>
              {t("own_chats.people.owner")}
            </Badge>
          )}
          {person.account_seq_no !== null && (
            <Badge tone="info">
              {t("own_chats.people.ours", { no: formatAccountNo(person.account_seq_no) })}
            </Badge>
          )}
          {person.is_bot && <Badge>{t("own_chats.people.bot")}</Badge>}
          {person.rank && <Badge>{person.rank}</Badge>}
        </span>
        <span className="block truncate text-xs text-fg-muted">
          {[person.username ? `@${person.username}` : null, note].filter(Boolean).map((part, i) => (
            <span key={i}>
              {i > 0 && " · "}
              {part}
            </span>
          ))}
        </span>
      </span>
      {actions}
    </li>
  );
}

const LIST = "divide-y divide-border rounded-xl border border-border bg-bg-primary";
const EMPTY =
  "rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-fg-muted";

/* ------------------------------------------------------------------ admins */

export function Admins({ chat, active }: { chat: OwnChat; active: boolean }) {
  const { t } = useTranslation();
  const admins = useChatAdmins(chat.id, active);
  const { data: me } = useCurrentUser();
  // The server lets only the company's admin hand a chat over (a superadmin
  // working inside the company is one).
  const mayTransfer = me?.role === "company_admin" || Boolean(me?.impersonating);
  const remove = useRemoveChatAdmin();
  const transfer = useTransferOwnChat();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Person | null>(null);
  const [removing, setRemoving] = useState<Person | null>(null);
  const [handing, setHanding] = useState<Person | null>(null);
  const relevant = ADMIN_RIGHTS[chat.kind];

  const menu = (person: Person): ActionMenuItem[] => {
    const user = refOf(person);
    if (person.role !== "admin" || person.is_self || user === null) return [];
    const items: ActionMenuItem[] = [
      {
        label: t("own_chats.admins.edit"),
        icon: <ShieldCheck size={15} strokeWidth={2} aria-hidden="true" />,
        onSelect: () => setEditing(person),
      },
      {
        label: t("own_chats.admins.remove"),
        icon: <ShieldOff size={15} strokeWidth={2} aria-hidden="true" />,
        onSelect: () => setRemoving(person),
      },
    ];
    if (chat.is_creator && mayTransfer && !person.is_bot) {
      items.push({
        label: t("own_chats.admins.transfer"),
        icon: <Crown size={15} strokeWidth={2} aria-hidden="true" />,
        onSelect: () => setHanding(person),
        destructive: true,
        separatorBefore: true,
      });
    }
    return items;
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* Wide enough to push the buttons to their own line on a phone. */}
        <p className="min-w-[12rem] flex-1 text-xs leading-relaxed text-fg-muted">
          {t(`own_chats.admins.hint_${chat.kind}`)}
        </p>
        <RereadButton query={admins} />
        <Button
          size="sm"
          onClick={() => setAdding(true)}
          iconLeft={<UserPlus size={15} strokeWidth={2} aria-hidden="true" />}
        >
          {t("own_chats.admins.add")}
        </Button>
      </div>

      <Live query={admins}>
        {(people) => (
          <ul className={LIST}>
            {people.map((person) => {
              const items = menu(person);
              const granted = relevant.filter((key) => person.rights?.[key]).length;
              return (
                <PersonLine
                  key={person.tg_user_id}
                  person={person}
                  note={
                    person.role === "creator"
                      ? t("own_chats.admins.all_rights")
                      : t("own_chats.admins.rights_of", { granted, total: relevant.length })
                  }
                  actions={
                    items.length > 0 ? (
                      <ActionMenu label={t("own_chats.actions")} items={items} />
                    ) : null
                  }
                />
              );
            })}
          </ul>
        )}
      </Live>

      <AdminDialog chat={chat} open={adding} onOpenChange={setAdding} person={null} />
      <AdminDialog
        chat={chat}
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        person={editing}
      />

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        tone="danger"
        title={t("own_chats.admins.remove_confirm", { name: removing?.name ?? "" })}
        description={t("own_chats.admins.remove_confirm_hint")}
        confirmText={t("own_chats.admins.remove")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          const user = removing && refOf(removing);
          if (user) await settle(remove, { id: chat.id, user });
        }}
      />

      <ConfirmDialog
        open={handing !== null}
        onOpenChange={(o) => !o && setHanding(null)}
        tone="danger"
        title={t("own_chats.transfer.confirm", { name: handing?.name ?? "", chat: chat.title })}
        description={t("own_chats.transfer.confirm_hint")}
        confirmText={t("own_chats.transfer.submit")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          const user = handing && refOf(handing);
          if (!user) return;
          const ours = handing?.account_seq_no != null;
          await settle(transfer, { id: chat.id, user }, () =>
            pushToast({
              kind: "success",
              text: t("own_chats.transfer.done"),
              detail: ours ? t("own_chats.transfer.done_ours") : undefined,
            }),
          );
        }}
      />
    </div>
  );
}

/**
 * Make somebody an admin, or — with a `person` — change the rights of one.
 * A new admin is one of the company's accounts (it joins the chat by itself
 * first) or anybody by @username.
 */
export function AdminDialog({
  chat,
  open,
  onOpenChange,
  person,
}: {
  chat: OwnChat;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  person: Person | null;
}) {
  const { t } = useTranslation();
  const accounts = useAccounts();
  const save = useSetChatAdmin();
  const [way, setWay] = useState("account");
  const [accountId, setAccountId] = useState("");
  const [username, setUsername] = useState("");
  const [rights, setRights] = useState<AdminRights>(() => defaultAdminRights(chat.kind));
  const [rank, setRank] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Every opening starts from the person's rights as they are, or the defaults.
  useEffect(() => {
    if (!open) return;
    setAccountId("");
    setUsername("");
    setRights(person?.rights ?? defaultAdminRights(chat.kind));
    setRank(person?.rank ?? "");
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const candidates = useMemo(
    () =>
      (accounts.data ?? [])
        .filter((a) => canAct(a) && a.id !== chat.account_id)
        .sort((a, b) => a.seq_no - b.seq_no),
    [accounts.data, chat.account_id],
  );
  const name = username.trim().replace(/^@/, "");
  // One of our accounts comes in by the public name or the invite link.
  const noWayIn = !person && way === "account" && !chat.username && !chat.invite_link;
  const target: AdminTarget | null = person
    ? ((user) => (user ? { user } : null))(refOf(person))
    : way === "account"
      ? accountId
        ? { account_id: accountId }
        : null
      : name
        ? { username: name }
        : null;

  const submit = async () => {
    if (!target) return;
    setError(null);
    try {
      await save.mutateAsync({ id: chat.id, rights, rank: rank.trim(), ...target });
      pushToast({
        kind: "success",
        text: t(person?.role === "admin" ? "own_chats.admins.saved" : "own_chats.admins.added"),
      });
      onOpenChange(false);
    } catch (err) {
      setError(errorText(err, t("own_chats.action_error")));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next || !save.isPending) && onOpenChange(next)}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>
            {person?.role === "admin"
              ? t("own_chats.admins.edit_title", { name: person.name })
              : person
                ? t("own_chats.admins.promote_title", { name: person.name })
                : t("own_chats.admins.add_title")}
          </DialogTitle>
          <DialogDescription>{chat.title}</DialogDescription>
          {!person && (
            <Tabs value={way} onValueChange={setWay} variant="pill" className="mt-3">
              <TabsList>
                <TabsTrigger value="account">{t("own_chats.admins.way_account")}</TabsTrigger>
                <TabsTrigger value="username">{t("own_chats.admins.way_username")}</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </DialogHeader>

        <DialogBody className="space-y-4">
          {!person && way === "account" && (
            <div className="space-y-2">
              <SimpleSelect
                value={accountId}
                onValueChange={setAccountId}
                placeholder={t("own_chats.field.account_placeholder") ?? undefined}
                disabled={accounts.isLoading || noWayIn}
                options={candidates.map((a) => ({
                  value: a.id,
                  label: accountOptionLabel(a, t("own_chats.account_not_warmed")),
                }))}
              />
              {noWayIn ? (
                <Alert tone="warning">{t("own_chats.admins.no_way_in")}</Alert>
              ) : (
                <p className="text-xs leading-relaxed text-fg-muted">
                  {t("own_chats.admins.account_hint")}
                </p>
              )}
            </div>
          )}
          {!person && way === "username" && (
            <div className="space-y-2">
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="@username"
                aria-label="@username"
                autoFocus
              />
              <p className="text-xs leading-relaxed text-fg-muted">
                {t("own_chats.admins.username_hint")}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label>{t("own_chats.admins.rights")}</Label>
            <ul className="space-y-1.5">
              {ADMIN_RIGHTS[chat.kind].map((key) => (
                <li key={key}>
                  <label className="flex cursor-pointer items-start gap-2 text-sm text-fg-primary">
                    <Checkbox
                      className="mt-0.5"
                      checked={rights[key]}
                      onCheckedChange={(v) => setRights((r) => ({ ...r, [key]: v === true }))}
                    />
                    {t(`own_chats.right.${chat.kind}.${key}`)}
                  </label>
                </li>
              ))}
            </ul>
          </div>

          {chat.kind === "group" && (
            <div className="space-y-2">
              <Label htmlFor="oc_rank">{t("own_chats.admins.rank")}</Label>
              <Input
                id="oc_rank"
                value={rank}
                maxLength={LIMITS.rank}
                onChange={(e) => setRank(e.target.value)}
                placeholder={t("own_chats.admins.rank_placeholder") ?? ""}
              />
            </div>
          )}

          {error && <Alert tone="danger">{error}</Alert>}
        </DialogBody>

        <DialogFooter>
          {save.isPending && (
            <span className="mr-auto text-xs text-fg-muted">
              {t(!person && way === "account" ? "own_chats.admins.joining" : "own_chats.working")}
            </span>
          )}
          <Button variant="outline" disabled={save.isPending} onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={!target || noWayIn || save.isPending}
            loading={save.isPending}
            onClick={submit}
          >
            {t(person?.role === "admin" ? "own_chats.save" : "own_chats.admins.add_submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------------------------------------------- members */

export function Members({ chat, active }: { chat: OwnChat; active: boolean }) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [show, setShow] = useState("members");
  const banned = show === "banned";
  const members = useChatMembers(chat.id, { q, banned }, active);
  const move = useMoveChatMember();
  const [promoting, setPromoting] = useState<Person | null>(null);
  const [leaving, setLeaving] = useState<{ person: Person; action: "kick" | "ban" } | null>(null);

  const menu = (person: Person): ActionMenuItem[] => {
    const user = refOf(person);
    if (user === null || person.is_self || person.role === "creator") return [];
    if (banned) {
      return [
        {
          label: t("own_chats.members.unban"),
          disabled: move.isPending,
          onSelect: () =>
            move.mutate(
              { id: chat.id, user, action: "unban" },
              {
                onSuccess: () =>
                  pushToast({ kind: "success", text: t("own_chats.members.unbanned") }),
              },
            ),
        },
      ];
    }
    const items: ActionMenuItem[] = [];
    if (person.role === "member" && !person.is_bot) {
      items.push({
        label: t("own_chats.members.promote"),
        icon: <ShieldCheck size={15} strokeWidth={2} aria-hidden="true" />,
        onSelect: () => setPromoting(person),
      });
    }
    items.push(
      {
        label: t("own_chats.members.kick"),
        icon: <UserMinus size={15} strokeWidth={2} aria-hidden="true" />,
        onSelect: () => setLeaving({ person, action: "kick" }),
        separatorBefore: items.length > 0,
      },
      {
        label: t("own_chats.members.ban"),
        icon: <UserX size={15} strokeWidth={2} aria-hidden="true" />,
        onSelect: () => setLeaving({ person, action: "ban" }),
        destructive: true,
      },
    );
    return items;
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={show} onValueChange={setShow} variant="pill">
          <TabsList>
            <TabsTrigger value="members">{t("own_chats.members.tab_members")}</TabsTrigger>
            <TabsTrigger value="banned">{t("own_chats.members.tab_banned")}</TabsTrigger>
          </TabsList>
        </Tabs>
        <form
          className="relative min-w-[10rem] flex-1"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(typed.trim());
          }}
        >
          <Search
            size={15}
            strokeWidth={2}
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted"
          />
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            maxLength={64}
            placeholder={t("own_chats.members.search") ?? ""}
            aria-label={t("own_chats.members.search")}
            className="h-9 pl-9"
          />
        </form>
        <RereadButton query={members} />
      </div>
      <p className="text-xs leading-relaxed text-fg-muted">{t("own_chats.members.hint")}</p>

      <Live query={members}>
        {(people) =>
          people.length === 0 ? (
            <p className={EMPTY}>
              {t(
                q
                  ? "own_chats.members.nothing_found"
                  : banned
                    ? "own_chats.members.empty_banned"
                    : "own_chats.members.empty",
              )}
            </p>
          ) : (
            <ul className={LIST}>
              {people.map((person) => {
                const items = menu(person);
                return (
                  <PersonLine
                    key={person.tg_user_id}
                    person={person}
                    note={person.role === "admin" ? t("own_chats.people.admin") : undefined}
                    actions={
                      items.length > 0 ? (
                        <ActionMenu label={t("own_chats.actions")} items={items} />
                      ) : null
                    }
                  />
                );
              })}
            </ul>
          )
        }
      </Live>

      <AdminDialog
        chat={chat}
        open={promoting !== null}
        onOpenChange={(o) => !o && setPromoting(null)}
        person={promoting}
      />

      <ConfirmDialog
        open={leaving !== null}
        onOpenChange={(o) => !o && setLeaving(null)}
        tone="danger"
        title={t(`own_chats.members.${leaving?.action ?? "kick"}_confirm`, {
          name: leaving?.person.name ?? "",
        })}
        description={t(`own_chats.members.${leaving?.action ?? "kick"}_confirm_hint`)}
        confirmText={t(`own_chats.members.${leaving?.action ?? "kick"}`)}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          const user = leaving && refOf(leaving.person);
          if (leaving && user) {
            await settle(move, { id: chat.id, user, action: leaving.action });
          }
        }}
      />
    </div>
  );
}
